import { useMemo } from 'react';
import {
  parseDate,
  isValidDate,
  calculateResponseTime,
  calculateResolutionTime,
  calculateSLAStatus,
} from '../../../utils/slaDashboardUtils';
import { AT_RISK_HOURS_THRESHOLD as THRESHOLD } from '../../../constants/slaDashboard';

/**
 * Hook to calculate SLA metrics
 * @param {Array} queries - Array of queries
 * @param {Date} now - Current date
 * @returns {Object} SLA metrics
 */
export const useSLAMetrics = (queries, now) => {
  return useMemo(() => {
    if (!queries || queries.length === 0) {
      return {
        overallAdherence: 0,
        queriesWithinSLA: 0,
        queriesBreached: 0,
        queriesAtRisk: 0,
        averageResponseTime: 'N/A',
        averageResolutionTime: 'N/A',
        totalQueries: 0,
      };
    }

    let within = 0;
    let breached = 0;
    let atRisk = 0;
    let responseSum = 0;
    let responseCount = 0;
    let resolutionSum = 0;
    let resolutionCount = 0;
    let queriesWithSLA = 0;

    queries.forEach((query) => {
      // Parse dates with validation
      const due = parseDate(query.slaDueDate);
      const raisedAt = parseDate(query.raisedAt);
      const respondedAt = parseDate(query.respondedAt);
      const closedAt = parseDate(query.closedAt);
      const completedAt = respondedAt || closedAt;

      // Validate dates are valid
      if (due && !isValidDate(due)) {
        // console.warn('Invalid SLA due date for query:', query.queryId || query._id, query.slaDueDate);
        return;
      }
      if (raisedAt && !isValidDate(raisedAt)) {
        // console.warn('Invalid raisedAt date for query:', query.queryId || query._id, query.raisedAt);
        return;
      }

      // Calculate response time (time from raised to responded)
      const responseHours = calculateResponseTime(respondedAt, raisedAt);
      if (responseHours !== null) {
        responseSum += responseHours;
        responseCount += 1;
      }

      // Calculate resolution time (time from raised to closed)
      const resolutionHours = calculateResolutionTime(closedAt, raisedAt);
      if (resolutionHours !== null) {
        resolutionSum += resolutionHours;
        resolutionCount += 1;
      }

      if (!due || !isValidDate(due)) {
        return;
      }

      queriesWithSLA += 1;

      // Calculate SLA status
      const status = calculateSLAStatus(due, completedAt, now, THRESHOLD);

      if (status === 'Breached') {
        breached += 1;
      } else if (status === 'At Risk') {
        atRisk += 1;
      } else {
        within += 1;
      }
    });

    // Only count queries with SLA due dates for adherence calculation
    const totalQueries = queriesWithSLA;
    const overallAdherence = totalQueries > 0 ? Number(((within / totalQueries) * 100).toFixed(1)) : 0;
    const averageResponseTime =
      responseCount > 0 ? `${(responseSum / responseCount).toFixed(1)} hours` : 'N/A';
    const averageResolutionTime =
      resolutionCount > 0 ? `${(resolutionSum / resolutionCount).toFixed(1)} hours` : 'N/A';

    return {
      overallAdherence,
      queriesWithinSLA: within,
      queriesBreached: breached,
      queriesAtRisk: atRisk,
      averageResponseTime,
      averageResolutionTime,
      totalQueries,
    };
  }, [queries, now]);
};
